/*eslint no-prototype-builtins: "off" */
import React from 'react';
import * as ErrorDialog from 'components/ErrorView/ErrorDialog';
import {
  deleteItemLink,
  itemCreated,
  restoreItem,
  requireDocument,
  requireDocumentInFolder,
  updateItem,
} from 'store/providers/providerSlice';
import * as DocumentExport from 'store/entity/Document';
import { CollectionContent, CollectionItem } from './styles';
import messages from './Messages';
import DataItem from 'components/FileManager/DataClass/DataItem';
import MultiList from 'components/FileManager/MultiList';
import FolderHeader from './FolderHeader';
import FolderView from './FolderView';
import DocumentView from './DocumentView';
import QuillView from './QuillView';
import SurveyView from './SurveyView';
import ChartView from './ChartView';
import LinkView from './LinkView';
import ExternalLinkView from './ExternalLinkView';
import ReportView from './ReportView';
import SearchTagView from './SearchTagView';
import ItemLinkView from './ItemLinkView';
import MoveClipboard from './MoveClipboard';
import notify from 'components/Notify';
import unknownFileType from 'assets/images/unknown.svg';
import { navigate } from '@reach/router';
import { ItemViewTemplate } from './ItemView';
import {
  IProvider,
  IProviderItems,
  IProviderItemViewWithData,
  providerItemType,
} from 'store/providers/providerTypes';
import {
  isAdvanceAdmin,
  isCompanyAdmin,
  isCompanyOwnerOrSupport,
} from 'store/companies/companiesUtils';
import { IUsers } from '../../../../store/users/usersType';
import {
  ICompany,
  IProjectStore,
} from '../../../../store/companies/companiesTypes';
import { ITasks } from '../../../../store/tasks/tasksType';
import { IOpenItem } from '../../../../store/entity/IOpenItem';
import {
  IOpenItemsContext,
  OpenItemsContext,
} from '../../../../store/context/OpenItemsContext';

export interface ISearchTerm {
  type: string;
  value: number | string | undefined;
  fastAccess?: string | undefined;
}

interface IComponentProps {
  providerItems: IProviderItems;
  provider: IProvider;
  dispatch: (action: any) => any;
  expectedOrder: number;
  users: IUsers;
  isUserManager: boolean;
  company: ICompany;
  projects: IProjectStore;
  tasks: ITasks;
  userId: number;
  searchInFolderLoading?: boolean;
  searchTerm?: string;
  roleId?: string;
  rootFolderId?: number;
}

interface IComponentState {
  openFolders: ISearchTerm[];
  openItems: IOpenItem[];
  openItemId: string | null;
  moveItems: IProviderItemViewWithData[];
  numberOfColumns: number;
  showDeletedDocuments: boolean;
  url: string;
}

class DocumentManager extends React.PureComponent<
  IComponentProps,
  IComponentState
> {
  static contextType = OpenItemsContext;
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      // openFolders: [{type: 'search' | 'folder', value: searchTerm | itemId}]
      openFolders: [
        this.props.searchTerm
          ? { type: 'search', value: this.props.searchTerm }
          : {
              type: 'folder',
              value: this.props.rootFolderId,
              fastAccess: this.props.roleId,
            },
      ],
      openItems: [],
      openItemId: null,
      moveItems: [],
      numberOfColumns: 3,
      showDeletedDocuments: false,
      url: window.location.href,
    };
  }

  componentDidUpdate(prevProps: IComponentProps) {
    if (
      prevProps.rootFolderId !== this.props.rootFolderId &&
      this.props.providerItems?.items?.[Number(this.props.rootFolderId)] ===
        undefined &&
      !this.props.searchTerm
    ) {
      this.handleLoadItem(Number(this.props.rootFolderId));
    }

    const cntx = this.context;
    const currentUrl = window.location.href;
    if (currentUrl !== this.state.url) {
      this.setState({ url: currentUrl });
      const newUrl = new URL(currentUrl);
      const params = new URLSearchParams(newUrl.search);
      if (params.has('id')) {
        if (Number(this.state.openItemId) !== Number(params.get('id'))) {
          // @ts-expect-error jsx error
          cntx.changeOpenItems(
            Number(params.get('id')),
            this.props.provider.id
          );
        }
      } else {
        this.setState({ openItemId: null });
      }
    }
  }

  componentDidMount() {
    if (
      this.props.providerItems?.items?.[Number(this.props.rootFolderId)] ===
        undefined &&
      !this.props.searchTerm
    ) {
      this.handleLoadItem(this.props.rootFolderId as number);
    }
    // const managingCompanyId = this.props.provider.managingCompanyId;
    // if (managingCompanyId)
    //   this.props.dispatch(
    //     getCompanyHandler({
    //       companyId: managingCompanyId,
    //       templateStub: false,
    //       companyStub: false,
    //       projectStub: false,
    //       taskStub: false,
    //     })
    //   );

    this.handleUpdatedUrl();

    if (window.innerWidth <= 600) {
      window.addEventListener('popstate', () => this.handlePopState());
    }
  }

  componentWillUnmount() {
    if (window.innerWidth <= 600) {
      window.removeEventListener('popstate', this.handlePopState);
    }
  }

  handlePopState = () => {
    const { openFolders } = this.state;
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    if (
      openFolders.length > 1 &&
      openFolders.slice(-2, -1)[0].value === Number(params.get('id'))
    ) {
      const newOpenFolders = openFolders.slice(0, -1);
      this.setState({ openFolders: newOpenFolders });
    }
  };

  handleUpdatedUrl = () => {
    const cntx = this.context;
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    let isFolder = false;
    if (params.has('id')) {
      const id = Number(params.get('id'));
      this.getRecursivelyAncestorsIDs(id).then((res: any) => {
        if (res?.length > 0) {
          const listOfOpenFolders: ISearchTerm[] = [];
          res.reverse()?.map((item: string) => {
            listOfOpenFolders.push({
              type: 'folder',
              value: +item,
            });
          });
          isFolder = res.includes(id);
          this.setState({ openFolders: listOfOpenFolders });
        }
        if (!isFolder) {
          (cntx as any).changeOpenItems(id, this.props.provider.id);
        }
      });
    }
  };

  getRecursivelyAncestorsIDs = async (id: number): Promise<number[]> => {
    if (!id) return [];
    if (id === this.props.rootFolderId) {
      return [id];
    }
    await this.props.dispatch(requireDocument({ documentId: id }));

    const item = this.props.providerItems.items[id];
    if (item?.data?.type === 'folder' || item?.data?.type === 'searchTag') {
      if (item?.data?.dadId) {
        const ancestors = await this.getRecursivelyAncestorsIDs(
          item.data.dadId
        );
        return [item.data.id, ...ancestors];
      } else {
        return [item.data.id];
      }
    } else {
      const ancestors = await this.getRecursivelyAncestorsIDs(item.data.dadId);
      return ancestors;
    }
  };

  columnsChanged = (numberOfColumns: number) => {
    this.setState({ numberOfColumns });
  };
  static getDerivedStateFromProps(
    nextProps: IComponentProps,
    prevState: IComponentState
  ) {
    if (!nextProps.searchTerm) {
      if (
        !prevState.openFolders.find(
          (item) =>
            item.value === nextProps.rootFolderId &&
            item.fastAccess === nextProps.roleId
        )
      ) {
        // if root folder changed (provider changed)
        return {
          openFolders: [
            {
              type: 'folder',
              value: nextProps.rootFolderId,
              fastAccess: nextProps.roleId,
            },
          ],
        };
      }
    } else {
      if (
        !prevState.openFolders.find(
          (item) => item.value === nextProps.searchTerm
        )
      ) {
        // if root folder changed
        return {
          openFolders: [{ type: 'search', value: nextProps.searchTerm }],
        };
      }
    }

    // checkout ...
    const openItems = prevState.openItems.map((oldOpenItem) => {
      oldOpenItem.item =
        nextProps.providerItems.items[oldOpenItem.item.data.id];
      return oldOpenItem;
    });
    return { openItems };
  }

  // changeOpenItems = (itemId: number) => {
  //   this.props.dispatch(requireDocument({ documentId: itemId })).then(() => {
  //     const item = this.props.providerItems.items[itemId];
  //     if (item?.data?.type !== 'folder' && item?.data?.type !== 'searchTag') {
  //       const listOfOpenItemsId = this.state.openItems.map(
  //         (el) => el.item.data.id
  //       );
  //       const newOpenItem = new OpenItem(item, 'responses');
  //       this.setState({
  //         openItemId: itemId.toString(),
  //         openItems: listOfOpenItemsId.includes(item.data.id)
  //           ? this.state.openItems
  //           : [...this.state.openItems, newOpenItem],
  //       });
  //       return {
  //         openItemId: itemId,
  //         openItems: listOfOpenItemsId.includes(item.data.id)
  //           ? this.state.openItems
  //           : [...this.state.openItems, newOpenItem],
  //       };
  //     } else {
  //       this.setState({ openItemId: null });
  //       return null;
  //     }
  //   });
  // };

  getEmptyFolderComponent() {
    return (
      <li
        className="collection-item avatar valign-wrapper grey-text"
        style={{ padding: 'initial' }}
      >
        <div className="full-width">
          <span className="title center-align" style={{ display: 'block' }}>
            {messages.folderIsEmpty}
          </span>
        </div>
      </li>
    );
  }

  deleteProviderItemLink = (itemId: number, dadId: number) => {
    this.props.dispatch(
      deleteItemLink({
        linkId: itemId,
        sourceItemId: dadId,
        providerId: this.props.provider.id,
      })
    );
  };

  onRestoreItem = (itemId: number) => {
    this.props
      .dispatch(
        restoreItem({
          itemId: itemId,
        })
      )
      .then(() => {
        notify(messages.itemRestore);
        if (this.props.providerItems.items[itemId].data.type === 'folder') {
          this.handleLoadItemByDad(itemId);
        }
      });
  };

  getComponentFunction(
    item: IProviderItemViewWithData,
    items: IProviderItems['items'],
    templates: ItemViewTemplate[]
  ) {
    // @ts-expect-error jsx error
    const cntx: IOpenItemsContext = this.context;

    return (
      listIndex: number,
      selected: any,
      expectedOrder: number,
      isLink: { id: number; sourceItemId: number; description: string },
      isSearch: boolean,
      mandatoryTagIds?: number[]
    ) => {
      const props = this.props;
      const isOwnerOrSupport =
        props.provider.user === props.userId ||
        (this.props.company &&
          isCompanyOwnerOrSupport(props.userId, this.props.company));

      if (item.data.type === providerItemType.folder) {
        return (
          <FolderView
            expectedOrder={expectedOrder}
            selected={selected}
            listIndex={listIndex}
            item={item}
            onOpenFolder={this.handleOpenFolder}
            dispatch={props.dispatch}
            onCloseFolder={this.handleCloseFolder}
            users={props.users}
            onMoveItem={this.handleMoveItem.bind(this, item)}
            moveItems={this.state.moveItems}
            onPasteItems={this.handlePasteItem.bind(this, item, null)}
            templates={templates}
            provider={props.provider}
            isLink={isLink}
            items={items}
            onDeleteItemLink={this.deleteProviderItemLink}
            onRestoreItem={this.onRestoreItem}
            isSearch={isSearch}
            isOwner={isOwnerOrSupport}
          />
        );
      } else if (
        item.data.type === providerItemType.document &&
        item.data.document?.properties?.format ===
          DocumentExport.documentFormat.quill
      ) {
        return (
          <QuillView
            item={item}
            dispatch={props.dispatch}
            selected={selected}
            expectedOrder={expectedOrder}
            onOpenItem={cntx.handleOpenItem}
            onMoveItem={this.handleMoveItem.bind(this, item)}
            users={props.users}
            templates={templates}
            provider={props.provider}
            isLink={isLink}
            items={items}
            onDeleteItemLink={this.deleteProviderItemLink}
            onRestoreItem={this.onRestoreItem}
            isSearch={isSearch}
            isOwner={isOwnerOrSupport}
          />
        );
      } else if (item.data.type === providerItemType.document) {
        return (
          <DocumentView
            expectedOrder={expectedOrder}
            item={item}
            selected={selected}
            dispatch={props.dispatch}
            onMoveItem={this.handleMoveItem.bind(this, item)}
            users={props.users}
            onOpenItem={cntx.handleOpenItem}
            templates={templates}
            provider={props.provider}
            isLink={isLink}
            items={items}
            onDeleteItemLink={this.deleteProviderItemLink}
            onRestoreItem={this.onRestoreItem}
            isSearch={isSearch}
            isOwner={isOwnerOrSupport}
          />
        );
      } else if (item.data.type === providerItemType.survey) {
        return (
          <SurveyView
            item={item}
            dispatch={props.dispatch}
            selected={selected}
            expectedOrder={expectedOrder}
            onOpenItem={cntx.handleOpenItem}
            items={items}
            users={props.users}
            onMoveItem={this.handleMoveItem.bind(this, item)}
            templates={templates}
            provider={props.provider}
            isLink={isLink}
            onDeleteItemLink={this.deleteProviderItemLink}
            onRestoreItem={this.onRestoreItem}
            isSearch={isSearch}
            isOwner={isOwnerOrSupport}
            mandatoryTagIds={mandatoryTagIds}
          />
        );
      } else if (item.data.type === providerItemType.chart) {
        return (
          <ChartView
            item={item}
            dispatch={props.dispatch}
            selected={selected}
            expectedOrder={expectedOrder}
            onOpenItem={cntx.handleOpenItem}
            users={props.users}
            onMoveItem={this.handleMoveItem.bind(this, item)}
            templates={templates}
            items={items}
            provider={props.provider}
            isLink={isLink}
            onDeleteItemLink={this.deleteProviderItemLink}
            onRestoreItem={this.onRestoreItem}
            isSearch={isSearch}
            isOwner={isOwnerOrSupport}
          />
        );
      } else if (item.data.type === providerItemType.link) {
        return (
          <LinkView
            item={item}
            dispatch={props.dispatch}
            selected={selected}
            expectedOrder={expectedOrder}
            onOpenItem={cntx.handleOpenItem}
            users={props.users}
            onMoveItem={this.handleMoveItem.bind(this, item)}
            templates={templates}
            items={items}
            provider={props.provider}
            isLink={isLink}
            onDeleteItemLink={this.deleteProviderItemLink}
            onRestoreItem={this.onRestoreItem}
            isSearch={isSearch}
            isOwner={isOwnerOrSupport}
          />
        );
      } else if (item.data.type === providerItemType.externalLink) {
        return (
          <ExternalLinkView
            item={item}
            dispatch={props.dispatch}
            selected={selected}
            expectedOrder={expectedOrder}
            onOpenItem={cntx.handleOpenItem}
            users={props.users}
            items={items}
            onMoveItem={this.handleMoveItem.bind(this, item)}
            templates={templates}
            provider={props.provider}
            isLink={isLink}
            onDeleteItemLink={this.deleteProviderItemLink}
            onRestoreItem={this.onRestoreItem}
            isSearch={isSearch}
            isOwner={isOwnerOrSupport}
          />
        );
      } else if (item.data.type === providerItemType.searchTag) {
        return (
          <SearchTagView
            dispatch={props.dispatch}
            expectedOrder={expectedOrder}
            item={item}
            listIndex={listIndex}
            onMoveItem={this.handleMoveItem.bind(this, item)}
            onOpenFolder={this.handleOpenFolder}
            onOpenItem={cntx.handleOpenItem}
            provider={props.provider}
            selected={selected}
            items={items}
            templates={templates}
            users={props.users}
            isLink={isLink}
            onDeleteItemLink={this.deleteProviderItemLink}
            onRestoreItem={this.onRestoreItem}
            isSearch={isSearch}
            isOwner={isOwnerOrSupport}
          />
        );
      } else if (item.data.type === providerItemType.surveyReport) {
        return (
          <ReportView
            item={item}
            dispatch={props.dispatch}
            selected={selected}
            expectedOrder={expectedOrder}
            onOpenItem={cntx.handleOpenItem}
            users={props.users}
            onMoveItem={this.handleMoveItem.bind(this, item)}
            templates={templates}
            provider={props.provider}
            items={items}
            isLink={isLink}
            onDeleteItemLink={this.deleteProviderItemLink}
            onRestoreItem={this.onRestoreItem}
            isSearch={isSearch}
            isOwner={isOwnerOrSupport}
          />
        );
      } else if (item.data.type === providerItemType.itemLink) {
        return (
          <ItemLinkView
            item={item}
            dispatch={props.dispatch}
            selected={selected}
            expectedOrder={expectedOrder}
            onOpenItem={cntx.handleOpenItem}
            users={props.users}
            onMoveItem={this.handleMoveItem.bind(this, item)}
            templates={templates}
            provider={props.provider}
            items={items}
            isLink={isLink}
            onDeleteItemLink={this.deleteProviderItemLink}
            onRestoreItem={this.onRestoreItem}
            isSearch={isSearch}
            isOwner={isOwnerOrSupport}
          />
        );
      } else {
        return (
          <CollectionItem>
            <CollectionContent>
              <img src={unknownFileType} alt="" className="" />
              <div>
                <span className="title">
                  {'Invalid Type ' + item.data.type}
                </span>
                <p>{JSON.stringify(item.data)}</p>
              </div>
            </CollectionContent>
          </CollectionItem>
        );
      }
    };
  }

  goUp = () => {
    let { openFolders } = this.state;
    const { numberOfColumns } = this.state;
    const parent =
      this.props.providerItems.items?.[Number(this.props.rootFolderId)];
    const dadId = parent.data.dadId;
    if (dadId !== undefined) {
      openFolders.splice(0, 0, {
        type: 'folder',
        value: dadId,
        fastAccess: this.props.roleId,
      });
      openFolders = openFolders.slice(0, numberOfColumns || 3);
      navigate(`/content/${this.props.provider.id}/f/${dadId}`, {}).then(() => {
        this.setState({ openFolders });
      });
    }
  };

  getHeaderFunction = (item: IProviderItemViewWithData) => {
    return () => {
      if (
        item.data.type === providerItemType.folder ||
        item.data.type === providerItemType.searchTag
      ) {
        const { provider } = this.props;
        const { openFolders } = this.state;
        const isOwnerOrSupport =
          this.props.provider.user === this.props.userId ||
          (this.props.company &&
            isCompanyOwnerOrSupport(this.props.userId, this.props.company));
        const isRoot =
          provider.useVirtualSpaces && !isOwnerOrSupport
            ? item.data.dadId === provider.rootDocument.id
            : item.data.id === provider.rootDocument.id;
        const showGoUp = item.data.id === openFolders[0].value && !isRoot;

        return (
          <FolderHeader
            item={item}
            managingCompanyId={provider.managingCompanyId || undefined}
            userIsOwnerOrSupport={isOwnerOrSupport}
            userIsAdvancedAdmin={isAdvanceAdmin(
              this.props.userId,
              this.props.company
            )}
            limitAccessToCompany={provider.limitAccessToCompany}
            onPasteItems={
              this.state.moveItems.length > 0
                ? this.handlePasteItem.bind(this, item, null)
                : undefined
            }
            goUp={showGoUp ? this.goUp : undefined}
          />
        );
      }
      return null;
    };
  };

  handleShowDeletedItems = (
    showDeletedDocuments: boolean,
    idList: number[]
  ) => {
    this.setState({ showDeletedDocuments }, () => {
      if (showDeletedDocuments) {
        idList.map((val: number) => {
          return this.handleLoadItemByDad(val);
        });
      }
    });
  };

  handleMoveItem(item: IProviderItemViewWithData) {
    if (item) {
      this.setState({ moveItems: [item] });
    } else {
      this.setState({ moveItems: [] });
    }
  }

  handleCancelMove = () => {
    this.setState({ moveItems: [] });
  };

  handleReplaceQueryParam = (id: number) => {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    params.set('id', id.toString());
    if (window.innerWidth <= 600) {
      window.history.pushState(
        { path: currentUrl.pathname + '?' + params.toString() },
        '',
        currentUrl.pathname + '?' + params.toString()
      );
    } else {
      window.history.replaceState(
        { path: currentUrl.pathname + '?' + params.toString() },
        '',
        currentUrl.pathname + '?' + params.toString()
      );
    }
  };

  handleOpenFolder = (listIndex: number, itemId: number) => {
    this.handleReplaceQueryParam(itemId);
    const possibleReAdd = this.state.openFolders[listIndex];
    const newOpenFolders = this.state.openFolders.slice(0, listIndex);
    const item = this.props.providerItems.items[itemId];
    if (item) {
      const openFolder = { type: 'folder', value: itemId };
      newOpenFolders.push(openFolder);
    } else if (possibleReAdd) {
      newOpenFolders.push(possibleReAdd);
    }
    this.setState({
      openFolders: newOpenFolders,
    });
  };

  handleCloseFolder = (itemId: number) => {
    const itemIndex = this.state.openFolders.findIndex(
      (item) => item.value === itemId
    );
    if (itemIndex !== -1) {
      const newOpenDads = this.state.openFolders.slice(0, itemIndex);
      this.setState({
        openFolders: newOpenDads,
      });
    }
  };

  // this implementation is wrong
  handlePasteItem(
    targetItem: IProviderItemViewWithData,
    movedItems?: IProviderItemViewWithData[] | null
  ) {
    let sampleMovedItems = movedItems;
    if (!movedItems) {
      sampleMovedItems = this.state.moveItems;
    }
    const promiseList = [];
    const fileNames: string[] = [];
    if (sampleMovedItems) {
      for (let moveItem of sampleMovedItems) {
        moveItem = JSON.parse(JSON.stringify(moveItem));
        moveItem.data.dadId = targetItem.data.id;
        fileNames.push(moveItem.data.name as string);
        promiseList.push(
          this.props.dispatch(updateItem({ item: moveItem.data }))
        );
      }
    }
    Promise.all(promiseList)
      .then(() => {
        notify(
          messages.movedSuccessfully(
            fileNames,
            targetItem.data.name?.toString() as string
          )
        );
        this.setState({ moveItems: [] });
      })
      .catch((err) => {
        ErrorDialog.display(err);
        notify(
          messages.movedFailed(
            fileNames,
            targetItem.data.name?.toString() as string
          ),
          'error'
        );
      });
  }

  handleLoadItemByDad = (dadId: number) => {
    this.props
      .dispatch(
        requireDocumentInFolder({
          folderId: dadId,
          providerId: this.props.provider.id,
          includeDeleted: this.state.showDeletedDocuments,
          forceLoad: this.state.showDeletedDocuments,
        })
      )
      .catch((err: any) => {
        ErrorDialog.display(err);
      });
  };
  handleLoadItem = (id: number) => {
    this.props
      .dispatch(requireDocument({ documentId: id }))
      .catch((err: any) => {
        ErrorDialog.display(err);
      });
  };

  handelDropItem = (itemId: number, newOrder: number, newDadId: number) => {
    const draggedItem = JSON.parse(
      JSON.stringify(this.props.providerItems.items[itemId])
    );

    const newItemClone = {
      ...draggedItem,
      data: {
        ...draggedItem.data,
        order: newOrder,
        dadId: Number(newDadId),
      },
    };

    this.props.dispatch(
      itemCreated({
        localItem: newItemClone.data,
        remoteItem: newItemClone.data,
      })
    );

    return this.props
      .dispatch(updateItem({ item: newItemClone.data }))
      .catch((err: any) => {
        this.props.dispatch(
          itemCreated({
            localItem: draggedItem.data,
            remoteItem: draggedItem.data,
          })
        );
        return Promise.reject(err);
      });
  };

  handleMinimizeItem = (itemId: number) => {
    window.history.back();
    if (this.state.openItemId === itemId.toString()) {
      this.setState({ openItemId: null });
    }
    this.forceUpdate();
  };

  render() {
    const {
      tasks,
      roleId,
      company,
      projects,
      providerItems,
      searchInFolderLoading,
    } = this.props;
    let templates: any;
    if (company && company.status === 'loaded') {
      const canCreateProjects = Object.values(projects)?.filter((project) => {
        const member = project.myMembership;
        if (member) {
          return member.canCreate;
        }
        return false;
      });
      const canViewProjects = Object.values(projects || {})?.filter(
        (project) => {
          return Boolean(project.myMembership);
        }
      );

      templates = Object.values(company.templates || {})
        .filter(
          (companyTemplate) =>
            Boolean(companyTemplate.suggestOnDocumentManager) &&
            Boolean(
              canViewProjects.find((project) =>
                project.templates.find(
                  (it: any) => it.taskId === companyTemplate.taskId
                )
              )
            )
        )
        .reduce(
          (
            list: { id: number; title: string; canCreate: boolean }[],
            template
          ) => {
            list.push({
              id: template.taskId,
              title: tasks.data[template.taskId].taskName,
              canCreate: !!canCreateProjects.find((project) =>
                project.templates.find(
                  (it: any) => it.taskId === template.taskId
                )
              ),
            });
            return list;
          },
          []
        );
    }

    const listItems = {};
    const items = providerItems.items;
    if (items !== null) {
      Object.values(items).forEach((item) => {
        let order = item?.data?.order;
        if (order === 0 && typeof item?.data?.id === 'number') {
          order = item?.data?.id;
        }
        listItems[item.data.id] = new DataItem(
          item.data.id,
          item.data.dadId,
          order,
          this.getComponentFunction(item, items, templates),
          this.getHeaderFunction(item),
          item.data.name,
          this.getEmptyFolderComponent as any,
          item.data.editable,
          item.data.type as any,
          item.data.hidden,
          item.data.status
        );
      });
    }
    const finalOpenFolders = this.state.openFolders.filter(
      (v) => v.type !== 'folder' || listItems.hasOwnProperty(v.value as string)
    );
    return (
      <div style={{ height: '100%' }}>
        {this.state.moveItems.length > 0 && (
          <MoveClipboard
            moveItems={this.state.moveItems}
            handleCancelMove={this.handleCancelMove}
          />
        )}
        <MultiList
          openFolders={finalOpenFolders}
          searching={searchInFolderLoading as boolean}
          onLoadItemsByDad={this.handleLoadItemByDad}
          listItems={listItems}
          onOpenFolder={this.handleOpenFolder}
          onDrop={this.handelDropItem}
          searchItems={providerItems.search}
          folders={providerItems.folders}
          items={providerItems.items}
          roleId={roleId as string}
          columnsChanged={this.columnsChanged}
          rootFolderId={
            this.props.rootFolderId || this.props.provider?.rootDocument?.id
          }
          isAdmin={
            this.props.provider?.user === this.props.userId ||
            (this.props.company &&
              isCompanyAdmin(this.props.userId, this.props.company))
          }
          ShowDeletedItemHandler={this.handleShowDeletedItems}
          providerId={this.props.provider.id}
          isOwnerOrSupport={isCompanyOwnerOrSupport(
            this.props.userId,
            this.props.company
          )}
        />
      </div>
    );
  }
}

export default DocumentManager;
